import React, { useState, useRef, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '../Icon/Icon';
import { PermissionFlags } from '../PermissionFlags/PermissionFlags';
import menuIcon from '../../assets/images/burger-menu.svg';

interface SubmenuItem {
  text: string;
  href?: string;
  onClick?: () => void;
  isDisabled?: boolean;
  flags?: string[];
  icon?: ReactNode;
  name?: string;
}

interface MenuItem {
  text: string;
  href: string;
  name?: string;
  isDisabled?: boolean;
  flags?: string[];
  submenuItems?: SubmenuItem[];
  icon?: ReactNode;
}

interface Props {
  menuItems: MenuItem[];
}

export const ResponsiveMenu: React.FC<Props> = ({ menuItems }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [submenuIndex, setSubmenuIndex] = useState<number | null>(null);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const location = useLocation();
  
  const isActive = (href: string) => {
    return location.pathname === href;
  }
  
  const submenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleMenuButtonClick = () => {
    setIsOpen(!isOpen);
  };

  const handleSubmenuClick = (index: number) => {
    if (!menuItems[index].isDisabled) {
      setSubmenuIndex(submenuIndex === index ? null : index);
    }
  };

  const handleExpandClick = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (submenuRef.current && !submenuRef.current.contains(event.target as Node)) {
      setSubmenuIndex(null);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="flex items-center relative" ref={submenuRef}>
      {/* Desktop navigation */}
      <nav className="hidden md:flex">
        {menuItems.map((item, index) => (
          <PermissionFlags requiredFlags={item.flags} key={index}>
            <div className="relative">
            { item.submenuItems ? (
              <button
                onClick={() => handleSubmenuClick(index)}
                className={`px-4 py-1 border-b-2 border-transparent transition-colors duration-300 ${
                  item.isDisabled
                    ? 'cursor-not-allowed pointer-events-none text-gray-400'
                    : `hover:text-darker-gold cursor-pointer text-gray-700 hover-underline-animation ${
                      isActive(item.href) ? 'text-custom-orange font-bold' : ''
                    }`
                }`}
              >
                {item.icon }
                {item.text}
              </button>)
              : (<button
                    onClick={() => handleNavigation(item.href!)}
                    className={`w-full px-4 border-b-2 border-transparent py-1 transition-colors duration-300 ${
                      item.isDisabled
                        ? 'cursor-not-allowed pointer-events-none text-gray-400'
                        : `hover:text-darker-gold cursor-pointer text-gray-700 hover-underline-animation ${
                          isActive(item.href) ? 'text-custom-orange font-bold' : ''
                        }`
                    }`}
                  >
                    { item.icon }
                    {item.text}
                  </button> )}
              {submenuIndex === index && item.submenuItems && (
                <div className="absolute left-0 mt-1 bg-white border border-gray-200 shadow-md">
                  {item.submenuItems.map((subItem, subIndex) => (
                    <PermissionFlags requiredFlags={subItem.flags} key={subIndex}>
                      {subItem.href ? (
                        <button
                            onClick={() => handleNavigation(subItem.href!)}
                            className={`w-full px-4 py-1 border-b-2 border-transparent transition-colors duration-300 ${
                              subItem.isDisabled
                                ? 'cursor-not-allowed pointer-events-none text-gray-400'
                                : `hover:text-darker-gold cursor-pointer text-gray-700 hover-underline-animation ${
                                  isActive(subItem.href) ? 'text-custom-orange font-bold' : ''
                                }`
                            }`}
                          >
                            {subItem.icon}
                            {subItem.text}
                          </button>
                      ) : (
                        <button
                          onClick={subItem.onClick}
                          disabled={subItem.isDisabled}
                          className={`block px-4 py-1 border-b-2 border-transparent transition-colors duration-300 ${
                            subItem.isDisabled
                              ? 'text-gray-400 cursor-not-allowed pointer-events-none'
                              : `text-gray-700 hover:text-darker-gold cursor-pointer hover-underline-animation ${
                                isActive(item.href) ? 'text-custom-orange font-bold' : ''
                              }`
                          }`}
                        >
                          <div className="flex items-center">
                            {subItem.icon} &nbsp;
                            <span>{subItem.text}</span>
                          </div>
                        </button>
                      )}
                    </PermissionFlags>
                  ))}
                </div>
              )}
            </div>
          </PermissionFlags>
        ))}
      </nav>
      
      {/* Mobile menu button - positioned in first row */}
      <div className="md:hidden">
        {
          isOpen ?
          <button
            onClick={handleMenuButtonClick}
            className="focus:outline-none text-gray-700 px-2 py-1"
          >
            ✕
          </button>
          : 
          <Icon onClick={handleMenuButtonClick} cssStyles="w-6 h-6 focus:outline-none text-gray-700" url={menuIcon}/>
        }
      </div>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 md:hidden" onClick={handleMenuButtonClick} />
      )}
      {isOpen && (
        <nav className="fixed inset-0 flex flex-col items-start justify-start bg-white z-10 md:hidden overflow-y-auto">
          <div className="w-full flex items-center justify-between p-4 border-b border-gray-200 bg-white sticky top-0 z-20">
            <h2 className="text-lg font-semibold text-gray-800">Menu</h2>
            <button
              onClick={handleMenuButtonClick}
              className="focus:outline-none text-gray-700 hover:text-gray-900 text-2xl font-light leading-none">
              ×
            </button>
          </div>
          <div className="w-full pt-2">
            {menuItems.map((item, index) => (
              <PermissionFlags requiredFlags={item.flags} key={index}>
                <div className="w-full">
                  {item.submenuItems ? (
                    <>
                      <button
                        onClick={() => handleExpandClick(index)}
                        className={`w-full text-left px-6 py-4 transition-colors duration-300 border-b border-gray-100 flex items-center justify-between ${
                          item.isDisabled
                            ? 'cursor-not-allowed pointer-events-none text-gray-400'
                            : `hover:bg-gray-50 cursor-pointer text-gray-800 font-medium ${
                              isActive(item.href) ? 'text-custom-orange bg-gray-50' : ''
                            }`
                        }`}
                      >
                        <span className="flex items-center">
                          {item.icon && <span className="mr-3">{item.icon}</span>}
                          {item.text}
                        </span>
                        <span className={`text-gray-400 transition-transform duration-200 ${expandedIndex === index ? 'rotate-90' : ''}`}>
                          ›
                        </span>
                      </button>
                      {expandedIndex === index && (
                        <div className="bg-gray-50 border-b border-gray-100">
                          {item.submenuItems.map((subItem, subIndex) => (
                            <PermissionFlags key={subIndex} requiredFlags={subItem.flags}>
                              <button
                                onClick={() => {
                                  if (subItem.href) {
                                    handleNavigation(subItem.href);
                                  } else if (subItem.onClick) {
                                    subItem.onClick();
                                  }
                                  setIsOpen(false);
                                }}
                                className={`w-full text-left px-10 py-3 transition-colors duration-300 flex items-center ${
                                  subItem.isDisabled
                                    ? 'cursor-not-allowed pointer-events-none text-gray-400'
                                    : `hover:bg-gray-100 cursor-pointer text-gray-600 text-sm ${
                                      isActive(subItem.href || '') ? 'text-custom-orange font-medium bg-gray-100' : ''
                                    }`
                                }`}
                              >
                                {subItem.icon && <span className="mr-3 text-gray-400">{subItem.icon}</span>}
                                <span className="before:content-['•'] before:mr-2 before:text-gray-400">{subItem.text}</span>
                              </button>
                            </PermissionFlags>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <button
                      onClick={() => {
                        handleNavigation(item.href);
                        setIsOpen(false);
                      }}
                      className={`w-full text-left px-6 py-4 transition-colors duration-300 border-b border-gray-100 flex items-center ${
                        item.isDisabled
                          ? 'cursor-not-allowed pointer-events-none text-gray-400'
                          : `hover:bg-gray-50 cursor-pointer text-gray-800 font-medium ${
                            isActive(item.href) ? 'text-custom-orange bg-gray-50' : ''
                          }`
                      }`}
                    >
                      {item.icon && <span className="mr-3">{item.icon}</span>}
                      {item.text}
                    </button>
                  )}
                </div>
              </PermissionFlags>
            ))}
          </div>
        </nav>
      )}
    </div>
  );
};

